import { useState } from 'react';
import { FileText, FileType, Presentation, Download, Trash2, Loader2 } from 'lucide-react';
import { cn } from '@/lib/cn';
import { documentsApi } from '@/lib/api';

export type DocumentType = 'pdf' | 'pptx' | 'html' | 'docx';

interface DocumentCardProps {
  id: string;
  title: string;
  type: DocumentType;
  filename: string;
  createdAt?: Date | string;
  onDownload?: () => void;
  onDelete?: () => void;
  showDelete?: boolean;
}

const typeIcons: Record<DocumentType, typeof FileText> = {
  pdf: FileText,
  pptx: Presentation,
  html: FileType,
  docx: FileText,
};

const typeColors: Record<DocumentType, string> = {
  pdf: 'text-red-400',
  pptx: 'text-orange-400',
  html: 'text-blue-400',
  docx: 'text-blue-500',
};

export function DocumentCard({
  id,
  title,
  type,
  filename,
  createdAt,
  onDownload: onDownloadCb,
  onDelete,
  showDelete = true,
}: DocumentCardProps) {
  const [downloading, setDownloading] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const Icon = typeIcons[type] ?? FileText;
  const typeColor = typeColors[type] ?? 'text-white/60';

  const handleDownload = async () => {
    if (downloading) return;
    setDownloading(true);
    try {
      await documentsApi.download(id);
      onDownloadCb?.();
    } catch (e) {
      console.error('Download failed:', e);
    } finally {
      setDownloading(false);
    }
  };

  const handleDelete = async () => {
    if (deleting || !onDelete) return;
    setDeleting(true);
    try {
      await documentsApi.delete(id);
      onDelete();
    } catch (e) {
      console.error('Delete failed:', e);
    } finally {
      setDeleting(false);
    }
  };

  const dateStr = createdAt
    ? typeof createdAt === 'string'
      ? new Date(createdAt).toLocaleDateString()
      : createdAt.toLocaleDateString()
    : '';

  return (
    <div className="flex items-center gap-3 p-4 rounded-xl bg-white/5 border border-white/10">
      <div className={cn('p-2 rounded-lg bg-white/10', typeColor)}>
        <Icon className="w-6 h-6" />
      </div>

      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-white truncate">{title}</p>
        <p className="text-xs text-white/50">
          {type.toUpperCase()}{dateStr ? ` • ${dateStr}` : ''}
        </p>
      </div>

      <div className="flex gap-2">
        <button
          type="button"
          onClick={handleDownload}
          disabled={downloading}
          className="p-2 rounded-lg bg-cyan-500/20 text-cyan-300 hover:bg-cyan-500/30 disabled:opacity-50"
          title="Download"
        >
          {downloading ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <Download className="w-4 h-4" />
          )}
        </button>
        {showDelete && onDelete && (
          <button
            type="button"
            onClick={handleDelete}
            disabled={deleting}
            className="p-2 rounded-lg bg-red-500/20 text-red-400 hover:bg-red-500/30 disabled:opacity-50"
            title="Delete"
          >
            {deleting ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Trash2 className="w-4 h-4" />
            )}
          </button>
        )}
      </div>
    </div>
  );
}
